import React, { PropsWithChildren, ReactNode, useContext } from "react";
import { useScoreDetailsContext } from "../context/ScoreDetailsContext";
import { useTranslation } from "@root/misc/useTranslation";
import { useTeamImgProps } from "./teamImage";
import { Score } from "./Slot";
import _, { isNumber } from "lodash";
import { LineupOption, LineupSlotInfoWithPickedOption } from "../types";
import { LineupContext } from "../context/LineupContext";

interface Props {
  detailKey: string;
  amount: number;
  score: number;
}

const AthleteImage: React.FC<{
  imageUrl: string;
}> = ({ imageUrl }) => {
  const className = [
    "neo__scoreDetails__imageCircle",
    "neo__scoreDetails__optionImage",
  ].join(" ");

  return (
    <div className={className}>
      <div className="neo__scoreDetails__imageHolder">
        {imageUrl !== "" && (
          <img
            src={imageUrl}
          />
        )}
      </div>
    </div>
  );
};

const hasStats = (option: LineupOption) => {
  return (
    isNumber(option.stat_perc_picked) ||
    isNumber(option.stat_prev_score) ||
    isNumber(option.stat_score_sum)
  );
};

interface StatsBlockProps {
  option: LineupOption;
}

const StatsBlock = ({ option }: StatsBlockProps) => {
  if (!hasStats(option)) {
    return null;
  }

  return (
    <div className="neo__scoreDetails__statsBlock">
      {isNumber(option.stat_score_sum) && (
        <div className="neo__scoreDetails__statsBlock__entry">
          <span className="neo__scoreDetails__statsBlock__entryIcon neo__scoreDetails__statsBlock__entryIcon--scoreSum" />
          <span className="neo__scoreDetails__statsBlock__entryText">
            {option.stat_score_sum}
          </span>
        </div>
      )}
      {isNumber(option.stat_prev_score) && (
        <div className="neo__scoreDetails__statsBlock__entry">
          <span className="neo__scoreDetails__statsBlock__entryIcon neo__scoreDetails__statsBlock__entryIcon--prevScore" />
          <span className="neo__scoreDetails__statsBlock__entryText">
            {option.stat_prev_score}
          </span>
        </div>
      )}
      {isNumber(option.stat_perc_picked) && (
        <div className="neo__scoreDetails__statsBlock__entry">
          <span className="neo__scoreDetails__statsBlock__entryIcon neo__scoreDetails__statsBlock__entryIcon--percPicked" />
          <span className="neo__scoreDetails__statsBlock__entryText">
            {option.stat_perc_picked}%
          </span>
        </div>
      )}
    </div>
  );
};

const Header: React.FC<{slotInfo: LineupSlotInfoWithPickedOption, forceClose: () => void}> = ({slotInfo, forceClose}) => {
  const teamImgProps = useTeamImgProps();
  const { t } = useTranslation();
  const posLabel = t(`lineup.position.${slotInfo.pickedOption.position}`);
  
  return (
    <div className="neo__scoreDetails__header">
      <div className="neo__scoreDetails__topBar">
        {slotInfo.score_info && (
          <div className="neo__scoreDetails__positionScore">
            <Score
              score={slotInfo.score_info?.score || 0}
              markNotPartOfScoring={!slotInfo.score_info.part_of_scoring}
              size={"regular"}
            />
          </div>
        )}
        <div className="neo__scoreDetails__positionImage">
          {
            <>
              <AthleteImage
                imageUrl={slotInfo.pickedOption.image_url}
              />
            </>
          }
          {
          <div
            className={`neo__scoreDetails__athleteNameAndCountry${
              slotInfo.pickedOption.german_license ? " neo__scoreDetails__athleteNameAndCountry--german" : ""
            }`}
          >
            <span>
              <span>{slotInfo.pickedOption.short_name || slotInfo.pickedOption.name}</span>
              <span>{slotInfo.pickedOption.country_abbr}</span>
            </span>
            {slotInfo.pickedOption.image_url !== "" && (
              <img {...teamImgProps(slotInfo.pickedOption.team)} />
            )}
          </div>
          }
        </div>
        <div className="neo__scoreDetails__positionCloseIcon">
          <a
            className="neo__scoreDetails__closeIcon"
            role="button"
            title={t("score_details.action_close")}
            onClick={forceClose}
          />
        </div>
      </div>

      <StatsBlock option={slotInfo.pickedOption} />
    </div>
  );
}

export const ScoreDetailRow: React.FC<Props> = ({ detailKey, amount, score }) => {
  const { t } = useTranslation();

  return (
    <div className={`neo__scoreDetails__detail${amount > 1 ? " neo__scoreDetails__detail--bold" : ""}`}>
      <span>{amount}x</span>
      <span>{t(`score_details.event.${detailKey}`)}</span>
      <span>{score} P</span>
    </div>
  );
}

export const ScoreDetailTotal: React.FC<{score: number}> = ({ score }) => {
  const { t } = useTranslation();

  return (
    <div className="neo__scoreDetails__detail neo__scoreDetails__detail--total">
      <span></span>
      <span>{t(`score_details.total`)}</span>
      <span>{score} P</span>
    </div>
  );
}

export const ScoreDetails = () => {
  const { slotInfo, forceClose } = useScoreDetailsContext();
  const { t } = useTranslation();
  const { lineup } = useContext(LineupContext);

  if (!slotInfo) {
    return null;
  }

  return (
    <div className="neo__scoreDetails">
      <div className="neo__scoreDetails__backdrop" onClick={forceClose} />
      <div className="neo__scoreDetails__panel">
        <Header
          slotInfo={slotInfo}
          forceClose={forceClose}
        />
        <div className="neo__scoreDetails__matchday">
          <span>{t("score_details.matchday", { matchday: lineup?.round_id})}</span>
        </div>
        {
          slotInfo?.score_info?.score && slotInfo?.score_info?.score != 0 ?
          <div className="neo__scoreDetails__list">
            { _.map(slotInfo.score_info?.score_details, (detail) => {
              return (
                <ScoreDetailRow
                  key={detail.key}
                  detailKey={detail.key}
                  amount={detail.amount}
                  score={detail.score}
                />
              );
            })}
            {
              slotInfo.score_info ?
                <ScoreDetailTotal
                  score={slotInfo.score_info.score}
                />: undefined
            }
          </div> :
          <div className="neo__scoreDetails__noPoints">
            <span>{t("score_details.no_points_yet")}</span>
          </div>
        }
      </div>
    </div>
  );
};
